import type { CourseProgramChapterView } from "@pollen/core/model/course.model";
import type { I18nMessage } from "@pollen/core/model/i18n.model";
import type { MaybeRef } from "vue";

import type { CoursePageApiView } from "~~/model/course.model";

export type CourseProgramEnhancedEntryView = {
  title: I18nMessage;
  subtitle?: I18nMessage;
  icon: string;
  chapters: CourseProgramChapterView[];
  type: "MORNING" | "AFTERNOON" | "LUNCH" | "DATE";
};

type CourseProgramEnhancedParameters = {
  course: Pick<CoursePageApiView, "format" | "content_type" | "program">;
  session?: DatabaseTable<"course_sessions"> | null;
};

function toEnhancedEntries({
  course,
  session,
}: CourseProgramEnhancedParameters) {
  const { t } = useI18n();
  const entries: CourseProgramEnhancedEntryView[] = [];

  if (session && session.schedule.length > 1) {
    session.schedule.forEach((scheduleEntry, index) => {
      entries.push({
        title: `${t("course.program.time_slot.date", { index: index + 1 })} - ${useI18nDate(scheduleEntry.start_date!).value}`,
        icon: "ph:calendar-blank",
        chapters: [],
        type: "DATE",
      });
    });
  } else if (course.format.amount !== null && course.format.amount > 1) {
    Array.from({ length: course.format.amount }).forEach((_, index) => {
      entries.push({
        title: t("course.page.program.time_slot.date", { index: index + 1 }),
        icon: "ph:calendar-blank",
        chapters: [],
        type: "DATE",
      });
    });
  } else if (course.format.duration === "P1D") {
    entries.push(
      {
        title: t("course.program.time_slot.morning"),
        icon: "ph:sun-horizon",
        chapters: [],
        type: "MORNING",
      },
      {
        title: t("course.program.time_slot.afternoon"),
        icon: "ph:sun",
        chapters: [],
        type: "AFTERNOON",
      },
    );
  } else {
    entries.push({
      title: t("course.program.time_slot.morning"),
      icon: "ph:sun-horizon",
      chapters: [],
      type: "MORNING",
    });
  }

  const splitProgram = splitArray(course.program, entries.length);

  entries.forEach((entry, index) => {
    entry.chapters = splitProgram[index] ?? [];
  });

  return entries;
}

function isLunchIncluded({ course, session }: CourseProgramEnhancedParameters) {
  if (session && session.schedule.length > 1) return false;

  if (
    session &&
    session.schedule.length === 1 &&
    session.options.lunch_included
  )
    return true;

  return course.format.duration === "P1D" || course.format.duration === "P0.5D";
}

export function useCourseProgramEnhanced(
  params: MaybeRef<CourseProgramEnhancedParameters>,
) {
  const { t } = useI18n();

  const LUNCH_ENTRY: CourseProgramEnhancedEntryView = {
    title: t("course.program.time_slot.lunch"),
    subtitle: t("course.program.lunch_description"),
    icon: "ph:fork-knife",
    chapters: [],
    type: "LUNCH",
  };

  return computed(() => {
    const { course, session } = unref(params);

    let entries = toEnhancedEntries({ course, session }).filter(
      (entry) => entry.chapters.length > 0,
    );

    if (isLunchIncluded({ course, session })) {
      entries = entries.toSpliced(
        Math.ceil(entries.length / 2),
        0,
        LUNCH_ENTRY,
      );
    }

    return entries;
  });
}
